import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import {
    MDXEditor,
    headingsPlugin,
    listsPlugin,
    quotePlugin,
    thematicBreakPlugin,
    markdownShortcutPlugin,
    linkPlugin,
    linkDialogPlugin,
    imagePlugin,
    tablePlugin,
    codeBlockPlugin,
    codeMirrorPlugin,
    frontmatterPlugin,
    diffSourcePlugin,
    toolbarPlugin,
    DiffSourceToggleWrapper,
    UndoRedo,
    BoldItalicUnderlineToggles,
    BlockTypeSelect,
    ListsToggle,
    Separator,
    CreateLink,
    InsertImage,
    InsertThematicBreak,
    InsertTable,
} from "@mdxeditor/editor";

import { EditorState, StateEffect } from "@codemirror/state";
import "@mdxeditor/editor/style.css";
import "./SbMarkdownEditor.scss";
import { VIEWMODE } from "constants/markdown.constants";
import SourceModeToolbar, { MergeFieldButton, findSourceCmView } from "./SourceModeToolbar";

// ─────────────────────────────────────────────────────────────────────────────

const CoreMarkdownEditor = forwardRef(({ initialMarkdown, onChange, className, viewMode, mergeFields = [], initialViewType = "rich-text", showInsertImage = true, lockImageUrls = false }, ref) => {
        const [markdown, setMarkdown] = useState(initialMarkdown ?? "");
        const editorRef = useRef(null);

        // outer div — keyboard events + merge field targeting
        const mdxRef = useRef(null); // MDXEditor instance — for imperative setMarkdown
        // Counts pending external setMarkdown calls so we can suppress the
        // echoed onChange they produce and avoid an update loop.
        const externalCount = useRef(0);

        useImperativeHandle(ref, () => ({
            setMarkdown: (md) => {
                externalCount.current += 1;
                setMarkdown(md);
                mdxRef.current?.setMarkdown(md);
                // Safety valve: if MDXEditor never fires onChange (e.g. same content),
                // decrement so the counter doesn't permanently block user edits.
                setTimeout(() => {
                    if (externalCount.current > 0) externalCount.current -= 1;
                }, 300);
            },
        }));

        const handleChange = (nextMarkdown) => {
            // Suppress the onChange that MDXEditor echoes back after an
            // imperative setMarkdown call — prevents the two-panel sync loop.
            if (externalCount.current > 0) {
                externalCount.current -= 1;
                return;
            }

        const currentImageCount = (markdown.match(/!\[.*?\]\(.*?\)/g) || []).length;
        const nextImageCount = (nextMarkdown.match(/!\[.*?\]\(.*?\)/g) || []).length;

        if (nextImageCount < currentImageCount) return;

        setMarkdown(nextMarkdown);
        if (typeof onChange === "function") {
            onChange(nextMarkdown);
        }
    };

    // Add keyboard event listener to prevent backspace/delete on images
        useEffect(() => {
            if (viewMode !== VIEWMODE.EDIT) return;

            const isImageElement = (element) => {
                if (!element || element.nodeType !== Node.ELEMENT_NODE) return false;
                return (
                    element.tagName === 'IMG' || 
                    element.querySelector('img') ||
                    element.getAttribute('data-editor-block-type') === 'image' ||
                    element.classList?.contains('_imageWrapper_')
                );
            };

            const findPreviousImageSibling = (node) => {
            let current = node;
            
            // Traverse up to find the parent paragraph or block element
            while (current && current.parentElement) {
                let prevSibling = current.previousSibling;
                
                // Check all previous siblings
                while (prevSibling) {
                    // Skip empty text nodes
                    if (prevSibling.nodeType === Node.TEXT_NODE && prevSibling.textContent.trim() === '') {
                        prevSibling = prevSibling.previousSibling;
                        continue;
                    }
                    
                    // Check if this sibling is an image
                    if (prevSibling.nodeType === Node.ELEMENT_NODE && isImageElement(prevSibling)) {
                        return true;
                    }
                    
                    // If we hit a non-empty text node or other element, stop
                    break;
                }
                
                // Move up to parent and check its siblings
                current = current.parentElement;
                
                // Stop at editor boundaries
                if (current.classList?.contains('mdxeditor-root-contenteditable')) {
                    break;
                }
            }
            
            return false;
        };

        const handleKeyDown = (e) => {
            // Only handle backspace and delete keys
            if (e.key !== 'Backspace' && e.key !== 'Delete') return;

            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0) return;

            const range = selection.getRangeAt(0);
            
            if (e.key === 'Backspace') {
                let node = range.startContainer;
                
                // Case 1: Cursor is at the start of a text node (offset = 0)
                if (node.nodeType === Node.TEXT_NODE && range.startOffset === 0) {
                    if (findPreviousImageSibling(node)) {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                    }
                }
                // Case 2: Cursor is in an element node
                else if (node.nodeType === Node.ELEMENT_NODE) {
                    if (isImageElement(node)) {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                    }
                }
            } else if (e.key === 'Delete') {
                let node = range.endContainer;
                
                // Case 1: Cursor is at the end of a text node
                if (node.nodeType === Node.TEXT_NODE && range.endOffset === node.length) {
                    let nextSibling = node.nextSibling;
                    
                    // Skip empty text nodes
                    while (nextSibling && nextSibling.nodeType === Node.TEXT_NODE && nextSibling.textContent.trim() === '') {
                        nextSibling = nextSibling.nextSibling;
                    }
                    
                    if (nextSibling && nextSibling.nodeType === Node.ELEMENT_NODE && isImageElement(nextSibling)) {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                    }
                }
                // Case 2: Cursor is in an element node
                else if (node.nodeType === Node.ELEMENT_NODE) {
                    if (isImageElement(node)) {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                    }
                }
            }
        };

        // Add event listener to the editor container
        const editorContainer = editorRef.current;
        if (editorContainer) {
            editorContainer.addEventListener('keydown', handleKeyDown, true);
        }

        return () => {
            if (editorContainer) {
                editorContainer.removeEventListener('keydown', handleKeyDown, true);
            }
        };
    }, [viewMode, markdown]);

        // Lock image markdown (e.g. the banner) in the CodeMirror SOURCE view: it stays
        // visible but any edit touching an ![..](..) range is rejected before it applies.
        // (Rich-text mode is already covered: image is a non-text node + the keydown guard.)
        useEffect(() => {
            if (!lockImageUrls || viewMode !== VIEWMODE.EDIT) return;
            const container = editorRef.current;
            if (!container) return;

            const filter = EditorState.transactionFilter.of((tr) => {
                if (!tr.docChanged) return tr;
                const doc = tr.startState.doc.toString();
                let blocked = false;
                tr.changes.iterChangedRanges((a, b) => {
                    for (const m of doc.matchAll(/!\[.*?\]\(.*?\)/g))
                        if (a < m.index + m[0].length && b > m.index) blocked = true;
                });
                return blocked ? [] : tr; // [] cancels the change
            });

            // Source view is recreated on source<->rich toggle, so inject whenever it appears.
            const seen = new WeakSet();
            const inject = () => {
                const v = findSourceCmView({ current: container });
                if (v && !seen.has(v)) { seen.add(v); v.dispatch({ effects: StateEffect.appendConfig.of(filter) }); }
            };
            inject();
            const obs = new MutationObserver(inject);
            obs.observe(container, { childList: true, subtree: true });
            return () => obs.disconnect();
        }, [lockImageUrls, viewMode]);

        return (
            <div ref={editorRef} className={className ?? "sb-markdown-editor"}>
                <MDXEditor
                ref={mdxRef}
                key={`mdx-editor-${viewMode}`}
                markdown={markdown}
                onChange={handleChange}
                readOnly={viewMode === VIEWMODE.VIEW}
                plugins={[
                    headingsPlugin(),
                    listsPlugin(),
                    quotePlugin(),
                    thematicBreakPlugin(),
                    markdownShortcutPlugin(),

                    // Links
                    linkPlugin(),
                    linkDialogPlugin(),

                    // Media
                    imagePlugin(),
                    tablePlugin(),

                    // Code
                    codeBlockPlugin({ defaultCodeBlockLanguage: "text" }),
                    codeMirrorPlugin({
                        codeBlockLanguages: {
                            js: "JavaScript",
                            javascript: "JavaScript",
                            ts: "TypeScript",
                            typescript: "TypeScript",
                            tsx: "TSX",
                            jsx: "JSX",
                            css: "CSS",
                            html: "HTML",
                            json: "JSON",
                            yaml: "YAML",
                            yml: "YAML",
                            python: "Python",
                            py: "Python",
                            sql: "SQL",
                            bash: "Bash",
                            shell: "Shell",
                            sh: "Shell",
                            xml: "XML",
                            markdown: "Markdown",
                            md: "Markdown",
                            mermaid: "Mermaid",
                            text: "Plain Text",
                            plaintext: "Plain Text",
                            "": "Plain Text",
                        }
                    }),

                    // Advanced
                    frontmatterPlugin(),
                        diffSourcePlugin({ viewMode: initialViewType }),
                       ...(viewMode === VIEWMODE.EDIT ? [
                        toolbarPlugin({
                            toolbarContents: () => (
                                <DiffSourceToggleWrapper
                                    SourceToolbar={<SourceModeToolbar editorRef={editorRef} mergeFields={mergeFields} showInsertImage={showInsertImage} />}
                                >
                                    <UndoRedo />
                                    <Separator />
                                    <BlockTypeSelect />
                                    <Separator />
                                    <BoldItalicUnderlineToggles />
                                    <Separator />
                                    <ListsToggle />
                                    <Separator />
                                    <CreateLink />
                                    {showInsertImage && <InsertImage />}
                                    <InsertThematicBreak />
                                    <InsertTable />
                                    {mergeFields.length > 0 && (
                                        <MergeFieldButton fields={mergeFields} editorRef={editorRef} />
                                    )}
                                </DiffSourceToggleWrapper>
                            )
                        })
                    ] : [])
                ]}
            />
        </div>
        );
    }
);

/**
 * Public, reusable SkillsBridge Markdown editor component.
 *
 * Props:
 * - initialMarkdown: string | undefined – initial markdown value
 * - onChange: (markdown: string) => void – callback whenever content changes
 * - className: string | undefined – optional wrapper class for the host element
 * - readOnly: boolean – if true, editor is in view-only mode (default: false)
 */
const SbMarkdownEditor = forwardRef(({ initialMarkdown, onChange, className, viewMode = VIEWMODE.EDIT, mergeFields = [], initialViewType = "rich-text", showInsertImage = true, lockImageUrls = false }, ref) => (
        <CoreMarkdownEditor
            ref={ref}
            initialMarkdown={initialMarkdown}
            onChange={onChange}
            className={className}
            viewMode={viewMode}
            mergeFields={mergeFields}
            initialViewType={initialViewType}
            showInsertImage={showInsertImage}
            lockImageUrls={lockImageUrls}
        />
    )
);

export default SbMarkdownEditor;
